import React, { useEffect, useRef, useState } from "react";

/**
 * PacketTopology
 * A CSS/SVG animated enterprise-network topology used in the hero.
 * Nodes: PC -> SW1 -> RT1 -> FW1 -> SRV (target).
 * A green packet travels the path continuously with a "RTT" indicator.
 */
const NODES = [
    { id: "pc", label: "PC", sub: "Client", x: 60, y: 170 },
    { id: "sw1", label: "SW1", sub: "VLAN 10", x: 190, y: 170 },
    { id: "rt1", label: "RT1", sub: "OSPF", x: 320, y: 100 },
    { id: "fw1", label: "FW1", sub: "Policies", x: 460, y: 170 },
    { id: "srv", label: "SRV", sub: "Target", x: 590, y: 170 },
];

const PATH = [
    ["pc", "sw1"],
    ["sw1", "rt1"],
    ["rt1", "fw1"],
    ["fw1", "srv"],
];

const NodeIcon = ({ type, x, y }) => {
    const common = { x: x - 18, y: y - 18, width: 36, height: 36, rx: 8 };
    return (
        <g>
            <rect {...common} fill="#0d1117" stroke="#23282f" />
            {type === "pc" && (
                <>
                    <rect x={x - 10} y={y - 8} width={20} height={12} rx={2} fill="none" stroke="#7ce73b" strokeWidth="1.4" />
                    <rect x={x - 5} y={y + 5} width={10} height={3} fill="#7ce73b" />
                </>
            )}
            {type === "sw1" && (
                <>
                    <rect x={x - 12} y={y - 5} width={24} height={10} rx={2} fill="none" stroke="#7ce73b" strokeWidth="1.4" />
                    <circle cx={x - 6} cy={y} r={1.4} fill="#7ce73b" />
                    <circle cx={x} cy={y} r={1.4} fill="#7ce73b" />
                    <circle cx={x + 6} cy={y} r={1.4} fill="#7ce73b" />
                </>
            )}
            {type === "rt1" && (
                <>
                    <circle cx={x} cy={y} r={8} fill="none" stroke="#7ce73b" strokeWidth="1.4" />
                    <path d={`M${x - 6},${y} L${x + 6},${y} M${x},${y - 6} L${x},${y + 6}`} stroke="#7ce73b" strokeWidth="1.4" />
                </>
            )}
            {type === "fw1" && (
                <>
                    <path
                        d={`M${x - 8},${y - 8} L${x + 8},${y - 8} L${x + 8},${y + 4} Q${x},${y + 12} ${x - 8},${y + 4} Z`}
                        fill="none"
                        stroke="#ff8a3d"
                        strokeWidth="1.4"
                    />
                </>
            )}
            {type === "srv" && (
                <>
                    <rect x={x - 9} y={y - 9} width={18} height={7} rx={1} fill="none" stroke="#7ce73b" strokeWidth="1.2" />
                    <rect x={x - 9} y={y + 1} width={18} height={7} rx={1} fill="none" stroke="#7ce73b" strokeWidth="1.2" />
                    <circle cx={x - 5} cy={y - 5.5} r={0.9} fill="#7ce73b" />
                    <circle cx={x - 5} cy={y + 4.5} r={0.9} fill="#7ce73b" />
                </>
            )}
        </g>
    );
};

const PacketTopology = () => {
    const [running, setRunning] = useState(true);
    const [rtt, setRtt] = useState("1.24");
    const [logs, setLogs] = useState([
        "> system ready.",
        "> click nodes to inspect. auto packet flow: ON.",
    ]);

    const findNode = (id) => NODES.find((n) => n.id === id);

    useEffect(() => {
        if (!running) return;
        const t = setInterval(() => {
            const v = (Math.random() * 1.4 + 0.9).toFixed(2);
            setRtt(v);
            setLogs((prev) => {
                const next = [
                    ...prev,
                    `> ping 10.10.20.100 : ${v} ms  reply from srv`,
                ].slice(-6);
                return next;
            });
        }, 2600);
        return () => clearInterval(t);
    }, [running]);

    const logRef = useRef(null);
    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [logs]);

    // Build combined path string for motionPath
    const pathD = (() => {
        let d = "";
        PATH.forEach(([a, b], i) => {
            const na = findNode(a);
            const nb = findNode(b);
            if (i === 0) d += `M ${na.x} ${na.y} `;
            d += `L ${nb.x} ${nb.y} `;
        });
        return d.trim();
    })();

    return (
        <div className="terminal p-0" data-testid="packet-topology">
            <div className="terminal-header">
                <span className="dot" style={{ background: "#ff5b5b" }} />
                <span className="dot" style={{ background: "#ffb020" }} />
                <span className="dot" style={{ background: "#7ce73b" }} />
                <span className="ml-2 font-mono">active_topology.sh</span>
                <span className="ml-auto text-brand font-mono text-[11px]">● LIVE</span>
            </div>

            <div className="p-4">
                <div className="rounded-lg border border-[#181c22] bg-[#07090c] p-3 relative overflow-hidden">
                    <div className="absolute inset-0 bg-grid-dense opacity-70 pointer-events-none" aria-hidden />
                    <svg viewBox="0 0 660 260" className="w-full h-[220px] md:h-[260px] relative">
                        {/* Links */}
                        {PATH.map(([a, b], i) => {
                            const na = findNode(a);
                            const nb = findNode(b);
                            return (
                                <line
                                    key={i}
                                    x1={na.x}
                                    y1={na.y}
                                    x2={nb.x}
                                    y2={nb.y}
                                    stroke="#2a3038"
                                    strokeWidth="1.6"
                                    strokeDasharray="3 5"
                                />
                            );
                        })}

                        {/* Trace path (green) */}
                        <path
                            d={pathD}
                            stroke="rgba(124,231,59,0.35)"
                            strokeWidth="1.8"
                            fill="none"
                        />

                        {/* Moving packet */}
                        <circle r="5" className="pkt">
                            <animateMotion dur="4.2s" repeatCount="indefinite" rotate="auto">
                                <mpath xlinkHref="#pkt-path" />
                            </animateMotion>
                        </circle>
                        <path id="pkt-path" d={pathD} fill="none" opacity="0" />

                        {/* Nodes */}
                        {NODES.map((n) => (
                            <g key={n.id}>
                                <NodeIcon type={n.id} x={n.x} y={n.y} />
                                <text
                                    x={n.x}
                                    y={n.y + 34}
                                    textAnchor="middle"
                                    className="font-mono"
                                    fill="#e7ecf1"
                                    fontSize="11"
                                    fontWeight="700"
                                >
                                    {n.label}
                                </text>
                                <text
                                    x={n.x}
                                    y={n.y + 48}
                                    textAnchor="middle"
                                    className="font-mono"
                                    fill="#6b7280"
                                    fontSize="9"
                                >
                                    {n.sub}
                                </text>
                            </g>
                        ))}
                    </svg>

                    <div className="absolute top-3 right-3 flex items-center gap-2 font-mono text-[11px] bg-black/40 border border-[#1b2028] rounded-md px-2 py-1">
                        <span className="pulse-dot" />
                        <span className="text-brand">PACKET SENT</span>
                        <span className="text-dim">·</span>
                        <span className="text-white">RTT: {rtt}ms</span>
                    </div>
                </div>

                {/* Console output */}
                <div
                    ref={logRef}
                    className="mt-3 rounded-lg border border-[#181c22] bg-[#06080b] p-3 font-mono text-[12px] text-[#b7c1cb] max-h-[110px] overflow-y-auto"
                    data-testid="packet-console"
                >
                    {logs.map((l, i) => (
                        <div key={i} className={l.includes("reply") ? "text-brand" : ""}>
                            {l}
                        </div>
                    ))}
                    <div className="blink" />
                </div>

                <div className="mt-3 flex items-center justify-between text-[11px] font-mono">
                    <span className="text-mute">wireshark_console_v1.44</span>
                    <button
                        onClick={() => setRunning((r) => !r)}
                        className="text-brand hover:underline"
                        data-testid="packet-toggle"
                    >
                        {running ? "❚❚ pause" : "▶ run"} packet flow
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PacketTopology;
